import { memo, useMemo, type FC, type PointerEvent } from "react";
import type { DifferentBoardPosition } from "../../../http/deckDesigner/types/configurations";
import type { PlaneBounds, PlaneCoordinate, PlaneScreenPoint, ToScreenPoint } from "./CartesianPlane";
import styles from "./DeckBoardsLayer.module.css";

export type DifferentBoardKey = "long" | "short";

export type DeckBoardSegment = {
  id: string;
  isDifferentWidth?: boolean;
  differentBoardDirection?: "horizontal" | "vertical";
  differentBoardKey?: DifferentBoardKey;
  differentBoardPosition?: DifferentBoardPosition;
  differentBoardRegion?: PlaneBounds;
  corners: PlaneCoordinate[];
};

type DeckBoardsLayerProps = {
  boardSegments: DeckBoardSegment[];
  onDifferentBoardPointerDown?: (event: PointerEvent<SVGPolygonElement>, segment: DeckBoardSegment) => void;
  onDifferentBoardPointerMove?: (event: PointerEvent<SVGPolygonElement>, segment: DeckBoardSegment) => void;
  onDifferentBoardPointerUp?: (event: PointerEvent<SVGPolygonElement>, segment: DeckBoardSegment) => void;
  toScreenPoint: ToScreenPoint;
};

type DraggableBoard = {
  points: string;
  segment: DeckBoardSegment;
};

const getScreenCorners = (segment: DeckBoardSegment, toScreenPoint: ToScreenPoint): PlaneScreenPoint[] =>
  segment.corners
    .map((corner, index) => toScreenPoint(corner, index))
    .filter((point): point is PlaneScreenPoint => point !== null);

// The same outline a <polygon points> draws, as one closed sub-path of a shared <path d>.
const getClosedSubPath = (points: PlaneScreenPoint[]): string =>
  `M${points.map((point) => `${point.pixelX},${point.pixelY}`).join(" L")}Z`;

const DeckBoardsLayer: FC<DeckBoardsLayerProps> = ({
  boardSegments,
  onDifferentBoardPointerDown,
  onDifferentBoardPointerMove,
  onDifferentBoardPointerUp,
  toScreenPoint,
}) => {
  // Drag support is opt-in per caller (e.g. the read-only bottom view never passes these) - without
  // a handler wired up, a segment must stay plain "pointer-events: none" like the rest of the layer,
  // not just skip the callback, otherwise it silently re-enables hit-testing/cursor for a drag that
  // doesn't do anything there.
  const isDraggable = !!onDifferentBoardPointerDown;

  // A deck easily has hundreds of boards: every one that can't be dragged is drawn as a sub-path of
  // a single path per fill (plain / different width) instead of as its own polygon. Only the
  // draggable different-width boards keep their own element, for their pointer handlers and hover.
  const { boardsPath, differentBoardsPath, draggableBoards } = useMemo(() => {
    const boardSubPaths: string[] = [];
    const differentBoardSubPaths: string[] = [];
    const draggable: DraggableBoard[] = [];

    boardSegments.forEach((segment) => {
      const points = getScreenCorners(segment, toScreenPoint);

      if (points.length === 0) {
        return;
      }

      if (isDraggable && segment.isDifferentWidth && segment.differentBoardKey) {
        draggable.push({ segment, points: points.map((point) => `${point.pixelX},${point.pixelY}`).join(" ") });
        return;
      }

      (segment.isDifferentWidth ? differentBoardSubPaths : boardSubPaths).push(getClosedSubPath(points));
    });

    return {
      boardsPath: boardSubPaths.join(""),
      differentBoardsPath: differentBoardSubPaths.join(""),
      draggableBoards: draggable,
    };
  }, [boardSegments, isDraggable, toScreenPoint]);

  if (boardSegments.length === 0) {
    return null;
  }

  return (
    <svg className={styles.layer} aria-hidden="true">
      {boardsPath && <path className={styles.board} d={boardsPath} />}
      {differentBoardsPath && <path className={`${styles.board} ${styles.differentBoard}`} d={differentBoardsPath} />}
      {draggableBoards.map(({ segment, points }) => (
        <polygon
          key={segment.id}
          className={`${styles.board} ${styles.differentBoard} ${styles.draggableDifferentBoard}`}
          points={points}
          onPointerDown={(event) => onDifferentBoardPointerDown?.(event, segment)}
          onPointerMove={(event) => onDifferentBoardPointerMove?.(event, segment)}
          onPointerUp={(event) => onDifferentBoardPointerUp?.(event, segment)}
          onPointerCancel={(event) => onDifferentBoardPointerUp?.(event, segment)}
        />
      ))}
    </svg>
  );
};

// Memoized: re-renders only when the boards, their drag handlers or the view change.
export default memo(DeckBoardsLayer);
